import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../features/auth/AuthProvider.js";
import { ApiClientError } from "../services/api-client.js";
import { fetchAnalyticsDashboard, type AnalyticsDashboard } from "../services/analytics-api.js";

const DAYS_OPTIONS = [7, 14, 30, 60, 90];

function toNumber(s: string): number {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function isPlatformRole(role: string): boolean {
  return role === "superadmin" || role === "platform-support";
}

/** Para una barra horizontal: el valor más alto se normaliza a 100 %. */
function barWidth(value: number, max: number): string {
  if (max <= 0) return "0%";
  return `${Math.min(100, Math.round((value / max) * 100))}%`;
}

export function AnalyticsPage(): JSX.Element {
  const { t } = useTranslation();
  const { token, user } = useAuth();

  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<AnalyticsDashboard | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const tenantQuery = useMemo(() => {
    if (user?.tenantId) return { tenantId: user.tenantId };
    return {};
  }, [user?.tenantId]);

  const isPlatform = !!user && isPlatformRole(user.role);

  const friendlyError = useCallback(
    (e: unknown): string => {
      if (e instanceof ApiClientError) {
        if (e.status === 401) return t("errors.unauthorized");
        if (e.status === 403) return t("errors.forbidden");
        if (e.status === 404) return t("errors.notFound");
        return e.message || t("errors.generic");
      }
      return t("errors.generic");
    },
    [t],
  );

  const load = useCallback(async () => {
    if (!token) return;
    setLoadState("loading");
    setErrorMsg(null);
    try {
      const result = await fetchAnalyticsDashboard(token, { ...tenantQuery, days });
      setData(result);
      setLoadState("ok");
    } catch (e) {
      setLoadState("error");
      setErrorMsg(friendlyError(e));
    }
  }, [token, tenantQuery, days, friendlyError]);

  useEffect(() => {
    void load();
  }, [load]);

  const maxOrdersByDay = useMemo(() => {
    if (!data) return 0;
    return data.ordersByDay.reduce((m, d) => Math.max(m, d.count), 0);
  }, [data]);

  const maxTopProductQty = useMemo(() => {
    if (!data) return 0;
    return data.topProducts.reduce((m, p) => Math.max(m, toNumber(p.totalQuantity)), 0);
  }, [data]);

  const maxWarehouseStock = useMemo(() => {
    if (!data) return 0;
    return data.warehouseStock.reduce((m, w) => Math.max(m, toNumber(w.totalQuantity) + toNumber(w.reservedQuantity)), 0);
  }, [data]);

  return (
    <div className="analytics-page">
      <header className="page-header">
        <h1>{t("portal.analytics.title")}</h1>
        <p className="text-muted">{t("portal.analytics.subtitle")}</p>
        {isPlatform && !user?.tenantId ? (
          <p className="banner-inline" role="note">
            {t("portal.analytics.platformGlobalHint")}
          </p>
        ) : null}
      </header>

      <section className="card filters-card">
        <div className="filters-row">
          <label className="field-inline">
            <span>{t("portal.analytics.windowDays")}</span>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {DAYS_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {t("portal.analytics.lastNDays", { count: d })}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void load()}
            disabled={loadState === "loading"}
          >
            {loadState === "loading" ? t("common.loading") : t("portal.analytics.refresh")}
          </button>
        </div>
      </section>

      {errorMsg ? <p className="form-error">{errorMsg}</p> : null}
      {loadState === "loading" ? <p className="text-muted">{t("common.loading")}</p> : null}

      {loadState === "ok" && data ? (
        <>
          <section className="card-grid summary-grid">
            <article className="card stat-card">
              <h3>{t("portal.analytics.totalOrders")}</h3>
              <p className="stat-value">{data.orderStats.total}</p>
              <p className="text-muted small">
                {t("portal.analytics.windowSummary", { days: data.scope.days })}
              </p>
            </article>
            <article className="card stat-card">
              <h3>{t("portal.analytics.statusBreakdown")}</h3>
              <ul className="meta-list">
                {Object.keys(data.orderStats.byStatus).length === 0 ? (
                  <li className="text-muted">{t("common.noResults")}</li>
                ) : (
                  Object.entries(data.orderStats.byStatus)
                    .sort(([, a], [, b]) => b - a)
                    .map(([status, count]) => (
                      <li key={status}>
                        <strong>
                          {t(`orderStatus.${status}`, { defaultValue: status })}:
                        </strong>{" "}
                        {count}
                      </li>
                    ))
                )}
              </ul>
            </article>
            <article className="card stat-card">
              <h3>{t("portal.analytics.warehousesCount")}</h3>
              <p className="stat-value">{data.warehouseStock.length}</p>
              <p className="text-muted small">{t("portal.analytics.warehousesCountHint")}</p>
            </article>
          </section>

          <section className="card table-section">
            <h2 className="section-title">{t("portal.analytics.topProducts")}</h2>
            {data.topProducts.length === 0 ? (
              <p className="text-muted empty-hint">{t("common.noResults")}</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("portal.products.sku")}</th>
                      <th>{t("portal.products.name")}</th>
                      <th className="num">{t("portal.analytics.quantity")}</th>
                      <th className="num">{t("portal.analytics.orderCount")}</th>
                      <th>{t("portal.analytics.share")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topProducts.map((p) => (
                      <tr key={p.productId}>
                        <td>
                          <code>{p.sku}</code>
                        </td>
                        <td>{p.name}</td>
                        <td className="num">{toNumber(p.totalQuantity).toFixed(2)}</td>
                        <td className="num">{p.orderCount}</td>
                        <td>
                          <div className="bar-wrap" aria-hidden="true">
                            <div
                              className="bar-fill"
                              style={{ width: barWidth(toNumber(p.totalQuantity), maxTopProductQty) }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card table-section">
            <h2 className="section-title">{t("portal.analytics.warehouseStock")}</h2>
            {data.warehouseStock.length === 0 ? (
              <p className="text-muted empty-hint">{t("common.noResults")}</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("portal.warehouses.code")}</th>
                      <th>{t("portal.warehouses.name")}</th>
                      <th className="num">{t("portal.analytics.totalQuantity")}</th>
                      <th className="num">{t("portal.analytics.reservedQuantity")}</th>
                      <th className="num">{t("portal.analytics.productLines")}</th>
                      <th>{t("portal.analytics.share")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.warehouseStock.map((w) => {
                      const total = toNumber(w.totalQuantity) + toNumber(w.reservedQuantity);
                      return (
                        <tr key={w.warehouseId}>
                          <td>
                            <code>{w.code}</code>
                          </td>
                          <td>{w.name}</td>
                          <td className="num">{toNumber(w.totalQuantity).toFixed(2)}</td>
                          <td className="num">{toNumber(w.reservedQuantity).toFixed(2)}</td>
                          <td className="num">{w.productLines}</td>
                          <td>
                            <div className="bar-wrap" aria-hidden="true">
                              <div className="bar-fill" style={{ width: barWidth(total, maxWarehouseStock) }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card table-section">
            <h2 className="section-title">{t("portal.analytics.ordersByDay")}</h2>
            {data.ordersByDay.length === 0 ? (
              <p className="text-muted empty-hint">{t("common.noResults")}</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("portal.analytics.date")}</th>
                      <th className="num">{t("portal.analytics.orders")}</th>
                      <th>{t("portal.analytics.share")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ordersByDay.map((d) => (
                      <tr key={d.date}>
                        <td className="nowrap">{d.date}</td>
                        <td className="num">{d.count}</td>
                        <td>
                          <div className="bar-wrap" aria-hidden="true">
                            <div className="bar-fill" style={{ width: barWidth(d.count, maxOrdersByDay) }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
